import {memo, ReactElement, useEffect, useState} from "react";
import {SimpleHoTCellObject, SimpleHoTCellStyle, SimpleHoTChange, SimpleHoTRow, SimpleHoTWrapper} from "../../../../admin/line-item-management/SimpleHoTWrapper";
import {Property} from "../../../../../contexts/properties/PropertiesContext";
import {getMonths} from "../../../../../utils/months-helper";
import {RentSubsidyInput, useGetRentSubsidySettingsLazyQuery, useSetRentSubsidyMutation, VersionType} from "../../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import "native-injects";

export interface RentSubsidySettingsProps {
    property: Property;
    forecastStartMonthIndex: number;
    versionType: VersionType.Reforecast | VersionType.Budget;
}

const COL_TO_MONTH: Record<string, number> = {
    "values.0": 0,
    "values.1": 1,
    "values.2": 2,
    "values.3": 3,
    "values.4": 4,
    "values.5": 5,
    "values.6": 6,
    "values.7": 7,
    "values.8": 8,
    "values.9": 9,
    "values.10": 10,
    "values.11": 11
};

function toRentSubsidyInput(change: SimpleHoTChange): RentSubsidyInput | null {
    const monthIndex = COL_TO_MONTH[change.col];
    if(monthIndex === undefined) {
        return null;
    }
    return {
        monthIndex: monthIndex,
        value: change.newValue?.toString() ?? "0",
    };
}

export function RentSubsidySettings(props: RentSubsidySettingsProps): ReactElement {
    const [getRentSubsidySettings, {data, loading}] = useGetRentSubsidySettingsLazyQuery({
        variables: {
            propertyId: props.property.id,
            budgetYear: props.property.budgetYear,
        },
        fetchPolicy: "no-cache"
    });
    const [setRentSubsidy, {loading: setRentSubsidyLoading}] = useSetRentSubsidyMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            getRentSubsidySettings({
                variables: {
                    propertyId: props.property.id,
                    budgetYear: props.property.budgetYear,
                },
            });
        },
    });
    const [simpleHoTRows, setSimpleHoTRows] = useState<SimpleHoTRow[]>([]);
    const [collectedChanges, setCollectedChanges] = useState<SimpleHoTChange[]>([]);
    const months = getMonths(props.property);

    useEffect(() => {
        getRentSubsidySettings({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.property.budgetYear,
            },
        });
    }, [props.property]);

    useEffect(() => {
        if(loading || !data) {
            return;
        }
        const subsidyPercentage = new Array(12).fill(0);
        const subsidyAmount = new Array(12).fill(0);
        const subsidyDataForVersionType = data.marketRentSubsidy.find(data => data.versionType === props.versionType);
        if(subsidyDataForVersionType) {
            for(let i = props.forecastStartMonthIndex; i < 12; i++) {
                subsidyPercentage[i] = subsidyDataForVersionType.subsidyPercentage[i];
                subsidyAmount[i] = subsidyDataForVersionType.subsidyAmount[i];
            }
        }
        const hotRows: SimpleHoTRow[] = [];
        hotRows.push({
            isCompound: false,
            values: {
                label: "Subsidized Percentage",
                values: subsidyPercentage,
            }
        });
        hotRows.push({
            isCompound: false,
            values: {
                label: "Subsidized Amount",
                values: subsidyAmount,
            }
        });
        setSimpleHoTRows(hotRows);
    }, [data, loading]);

    useEffect(() => {
        if(setRentSubsidyLoading || collectedChanges.length === 0) {
            return;
        }
        const subsidyPercentage = collectedChanges
            .filter(change => (change.row.values as SimpleHoTCellObject)["label"] === "Subsidized Percentage")
            .map(change => toRentSubsidyInput(change))
            .filterNull();
        const subsidyAmount = collectedChanges
            .filter(change => (change.row.values as SimpleHoTCellObject)["label"] === "Subsidized Amount")
            .map(change => toRentSubsidyInput(change))
            .filterNull();

        setCollectedChanges([]);
        setRentSubsidy({
            variables: {
                propertyId: props.property.id,
                budgetYear: props.property.budgetYear,
                versionType: props.versionType,
                subsidyPercentage: subsidyPercentage,
                subsidyAmount: subsidyAmount,
            },
        }).then(res => {
            if(res.data?.setMarketRentSubsidy) {
                toast.success("Rent subsidy settings saved");
                return;
            }
            toast.error("Failed to save rent subsidy settings");
        }).catch(() => {
            toast.error("Failed to save rent subsidy settings");
        });
    }, [collectedChanges, setRentSubsidyLoading]);

    function handleChange(changes: SimpleHoTChange[]): void {
        setCollectedChanges([...collectedChanges, ...changes]);
    }
    return <div>
        <MemoizedSimpleHoTWrapper
            dataLoading={loading || setRentSubsidyLoading}
            rows={simpleHoTRows}
            columns={[
                {data: "label"},
                {data: "values.0", type: "numeric", allowInvalid: false},
                {data: "values.1", type: "numeric", allowInvalid: false},
                {data: "values.2", type: "numeric", allowInvalid: false},
                {data: "values.3", type: "numeric", allowInvalid: false},
                {data: "values.4", type: "numeric", allowInvalid: false},
                {data: "values.5", type: "numeric", allowInvalid: false},
                {data: "values.6", type: "numeric", allowInvalid: false},
                {data: "values.7", type: "numeric", allowInvalid: false},
                {data: "values.8", type: "numeric", allowInvalid: false},
                {data: "values.9", type: "numeric", allowInvalid: false},
                {data: "values.10", type: "numeric", allowInvalid: false},
                {data: "values.11", type: "numeric", allowInvalid: false},
            ]}
            cellStyler={(_row, col, _rowData) => {
                const style: SimpleHoTCellStyle = {};
                if(col == 0 || col - 1 >= 0 && col - 1 < props.forecastStartMonthIndex) {
                    style.isReadOnly = true;
                }
                return style;
            }}
            hotProps={{
                renderAllRows: true,
                height: "140",
                colHeaders: ["", ...months],
                colWidths: [150, ...(new Array(12).fill(90))],
                autoRowSize: false,
                autoColumnSize: false,
                rowHeights: 40,
                undo: false
            }}
            onChange={handleChange}
        />
    </div>;
}


const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    // Everything but rows is static.
    return prevProps.rows === nextProps.rows;
});
